import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api, { resolveImageUrl } from '../lib/api';
import { Card } from './ui/card';
import { Trophy, Calendar, Flag, MapPin, Zap, ExternalLink } from 'lucide-react';

export default function RacingIntelWidget() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get('/racing/dashboard').then((res) => {
      if (!cancelled) setData(res.data);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  if (!data) return null;
  const hasAny =
    (data.latest_winners?.length || 0) +
    (data.upcoming_races?.length || 0) +
    (data.new_tracks?.length || 0) > 0;
  if (!hasAny) return null;

  return (
    <section className="py-16 bg-[#0A0A0A] border-t border-[#1a1a1a]" data-testid="racing-intel-widget">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="tag text-[#FF3B30] mb-2">Racing Intel</p>
            <h2 className="font-heading font-bold text-3xl text-white">Crown Vic Nation — Live Feed</h2>
            <p className="text-gray-400 text-sm mt-1">Aggregated from 30+ short tracks every 12 hours.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Latest Winners */}
          <div>
            <h3 className="text-white font-heading font-bold text-lg flex items-center gap-2 mb-3">
              <Trophy className="w-5 h-5 text-[#FF3B30]" />Latest Winners
            </h3>
            {data.latest_winners?.length ? (
              <div className="space-y-2">
                {data.latest_winners.slice(0, 4).map((r) => (
                  <Card key={r.id} className="bg-[#141414] border-[#262626] p-3 card-hover">
                    <div className="flex items-start gap-3">
                      {r.image_url && (
                        <img src={resolveImageUrl(r.image_url)} alt="" className="w-14 h-14 object-cover rounded flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-white text-sm font-bold truncate">{r.winner_name || 'Unknown'}</span>
                          {r.winner_car_number && <span className="text-gray-500 text-xs">#{r.winner_car_number}</span>}
                          {r.is_high_profile && (
                            <span className="tag bg-[#FF3B30]/20 text-[#FF3B30] px-1.5 py-0.5 rounded text-[9px]">
                              <Zap className="w-2.5 h-2.5 inline" />
                            </span>
                          )}
                        </div>
                        <p className="text-gray-400 text-xs mt-0.5 truncate">
                          {r.track_name}{r.track_state ? ` · ${r.track_state}` : ''}
                        </p>
                        {r.event_date && <p className="text-gray-600 text-[11px] mt-0.5">{r.event_date}</p>}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="bg-[#141414] border-[#262626] p-5 text-center">
                <p className="text-gray-500 text-sm">No winners tracked yet.</p>
              </Card>
            )}
          </div>

          {/* Upcoming */}
          <div>
            <h3 className="text-white font-heading font-bold text-lg flex items-center gap-2 mb-3">
              <Calendar className="w-5 h-5 text-blue-400" />Upcoming Races
            </h3>
            {data.upcoming_races?.length ? (
              <div className="space-y-2">
                {data.upcoming_races.slice(0, 4).map((r) => (
                  <Card key={r.id} className="bg-[#141414] border-[#262626] p-3 card-hover">
                    <div className="flex items-start gap-3">
                      <div className="w-14 h-14 rounded bg-[#1F1F1F] border border-[#262626] flex flex-col items-center justify-center flex-shrink-0">
                        <span className="text-gray-500 text-[9px] uppercase">
                          {r.event_date ? new Date(r.event_date + 'T00:00:00').toLocaleString('en-US', { month: 'short' }) : ''}
                        </span>
                        <span className="text-white font-bold text-base leading-none">
                          {r.event_date ? new Date(r.event_date + 'T00:00:00').getDate() : '?'}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-bold truncate">{r.event_name || 'Crown Vic Event'}</p>
                        <p className="text-gray-400 text-xs mt-0.5 flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          <span className="truncate">{r.track_name}{r.track_state ? ` · ${r.track_state}` : ''}</span>
                        </p>
                        {r.class_name && <p className="text-gray-600 text-[11px] mt-0.5 truncate">{r.class_name}</p>}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="bg-[#141414] border-[#262626] p-5 text-center">
                <p className="text-gray-500 text-sm">No upcoming events scheduled yet.</p>
              </Card>
            )}
          </div>

          {/* New Tracks */}
          <div>
            <h3 className="text-white font-heading font-bold text-lg flex items-center gap-2 mb-3">
              <Flag className="w-5 h-5 text-green-400" />New Tracks Adding Crown Vic
            </h3>
            {data.new_tracks?.length ? (
              <div className="space-y-2">
                {data.new_tracks.slice(0, 4).map((t) => (
                  <Card key={t.id} className="bg-[#141414] border-[#262626] p-3 card-hover">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-bold truncate">{t.track_name}</p>
                        <p className="text-gray-400 text-xs mt-0.5 truncate">{t.track_city}{t.track_state ? `, ${t.track_state}` : ''}</p>
                        {t.first_scheduled_race && <p className="text-[#FF3B30] text-[11px] mt-0.5">First race: {t.first_scheduled_race}</p>}
                      </div>
                      {t.source_url && (
                        <a href={t.source_url} target="_blank" rel="noreferrer" className="text-gray-500 hover:text-white flex-shrink-0">
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="bg-[#141414] border-[#262626] p-5 text-center">
                <p className="text-gray-500 text-sm">No announcements yet.</p>
              </Card>
            )}
          </div>
        </div>

        {data.repeat_winners && data.repeat_winners.length > 0 && (
          <div className="mt-6 pt-6 border-t border-[#1a1a1a]">
            <p className="text-gray-500 text-xs uppercase tracking-wider mb-3">Hot Streak (90d)</p>
            <div className="flex gap-3 flex-wrap">
              {data.repeat_winners.slice(0, 5).map((w, i) => (
                <div key={i} className="bg-[#141414] border border-[#262626] rounded px-3 py-1.5 text-sm">
                  <span className="text-white font-medium">{w.winner_name}</span>
                  {w.car_number ? <span className="text-gray-500 ml-1">#{w.car_number}</span> : null}
                  <span className="text-[#FF3B30] font-bold ml-2">{w.wins}W</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
